import cx from 'classnames';
import * as React from 'react';
import { useId } from '../helper/hooks';

export type ButtonProps = {
  /** variant of the button */
  variant?:
    | 'default'
    | 'primary'
    | 'primary-outline'
    | 'primary-alt'
    | 'dark'
    | 'white'
    | 'outline'
    | 'success'
    | 'danger'
    | 'info'
    | 'warning'
    | 'link';
  /** min width of the button */
  minWidth?: 'wide' | 'wider' | 'widest';
  /** states if button action is ongoing */
  isLoading?: boolean;
  /** the content of the button when loading  */
  loadingText?: React.ReactNode;
} & JSX.IntrinsicElements['button'];

/**
 * `Button` is a wrapper for `<button>` html element, it accepts **ALL** props a `<button>` element accepts in addition of props listed below.
 *
 * You can use `ref` to access the underlying `<button />` element
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function ButtonComp(
  { className, type = 'button', variant = 'default', minWidth, isLoading, loadingText, children, id, ...props },
  ref
) {
  const uniqueId = useId(id);

  return (
    <button
      className={cx(
        'btn',
        variant && `btn-${variant}`,
        props.disabled && 'disabled',
        minWidth && `btn-${minWidth}`,
        className
      )}
      type={type}
      id={id}
      key={`${uniqueId}--${type}`} // to fix the weird edge case that cause React fire wrong event when type is change. See https://github.com/facebook/react/issues/8554
      {...props}
      ref={ref}
    >
      {isLoading ? (
        <>
          {loadingText}
          <span className="loading-dots" style={{ display: 'inline-block', marginLeft: 0 }} />
        </>
      ) : (
        children
      )}
    </button>
  );
});
